import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { withTranslation } from "react-i18next";

import GlobalLoader from "../../../components/Common/GlobalLoader";
import TableContainerWarehouseRx from "../../../components/Common/TableContainerWarehouseRx";
import { getLoansHistory } from "../../../slice/thunks";

const LoanHistoryPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize] = useState(10);

  const settingsSelector = (state) => state.Settings;
  const selector = createSelector(settingsSelector, (s) => ({
    loansHistory: s.loansHistory,
    error: s.error,
  }));
  const { loansHistory, error } = useSelector(selector);

  const items = Array.isArray(loansHistory)
    ? loansHistory
    : loansHistory?.items || [];

  const tableTotal = loansHistory?.total_items || items.length;
  const tableTotalPage = loansHistory?.total_page || 1;

  const employeeName = items?.[0]?.employeeName || "";

  const loadLoansHistory = useCallback(
    (page = 1, term = "") => {
      if (!id) return;
      setIsLoading(true);
      const filters = {
        pageNumber: page,
        pageSize,
        term,
      };
      dispatch(getLoansHistory(id, filters))
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
      setCurrentPage(page);
    },
    [dispatch, id, pageSize]
  );

  useEffect(() => {
    loadLoansHistory(currentPage, searchTerm);
  }, [loadLoansHistory, currentPage, searchTerm]);

  const moneyCell = (info) => {
    const v = Number(info.getValue() ?? 0);
    if (Number.isNaN(v)) return info.getValue();
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columns = useMemo(
    () => [
      { header: props.t("requestDate"), accessorKey: "requestDate", enableColumnFilter: false },
      { header: props.t("amountbeforeloan"), accessorKey: "amountBeforeLoan", cell: moneyCell, enableColumnFilter: false },
      { header: props.t("askAmount"), accessorKey: "amount", cell: moneyCell, enableColumnFilter: false },
      { header: props.t("ownedAmount"), accessorKey: "amountAfterLoan", cell: moneyCell, enableColumnFilter: false },
      { header: props.t("amountpermonth"), accessorKey: "amountPerMonth", cell: moneyCell, enableColumnFilter: false },
      { header: props.t("number of month"), accessorKey: "numberMonth", enableColumnFilter: false },
    ],
    [props]
  );

  return (
    <div className="loan-history-content">
      <div className="container-fluid px-3 pb-5">
        <div className="heading-parent-sec d-flex align-items-center pt-4">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-link p-0 me-3"
            style={{ textDecoration: "none", color: "#0d6efd" }}
          >
            {props.t("payroll_management")}
          </button>
          <span style={{ fontWeight: "bold" }}> /&nbsp; </span>
          <h6 className="mb-0">
            {props.t("history_of_loans")} {employeeName ? `: ${employeeName}` : ""}
          </h6>
        </div>

        <div className="card border-0 row p-2 mt-3">
          {isLoading ? (
            <GlobalLoader />
          ) : error ? (
            <div className="alert alert-danger mt-2">{String(error)}</div>
          ) : (
            <TableContainerWarehouseRx
              columns={columns}
              data={items}
              tablePage={currentPage}
              tableTotal={tableTotal}
              tableTotalPage={tableTotalPage}
              onNextPage={(page) => loadLoansHistory(page, searchTerm)}
              isGlobalFilter={true}
              onSearch={(term) => {
                setSearchTerm(term);
                loadLoansHistory(1, term);
              }}
              SearchPlaceholder={props.t("search_by_amount_or_date")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(LoanHistoryPage);
